// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useVideoMembershipAccess } from '@/hooks/useVideoMembershipAccess';
import { supabase } from '@/integrations/supabase/client';

interface Video {
  id: string;
  title: string;
  youtube_id: string;
  description: string | null;
  thumbnail_url: string | null;
  sort_order: number;
}

const VideoHoopsWatch: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { hasAccess, loading: membershipLoading } = useVideoMembershipAccess('video_hoops_recaps');
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [videosLoading, setVideosLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch videos if user is authenticated and has access
    if (!authLoading && !membershipLoading && user && hasAccess) {
      fetchVideos();
    }
  }, [user, hasAccess, authLoading, membershipLoading]);

  const fetchVideos = async () => {
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('membership_slug', 'video_hoops_recaps')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        throw error;
      }
      
      setVideos(data || []);
      if (data && data.length > 0) {
        setSelectedVideo(data[0]);
      }
    } catch (error) {
      console.error('Failed to load videos:', error);
      setError('Failed to load videos. Please try again.');
    } finally {
      setVideosLoading(false);
    }
  };

  const getYouTubeThumbnail = (youtubeId: string) => {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  };

  const getYouTubeThumbnailFallback = (youtubeId: string) => {
    return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
  };

  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement, Event>, youtubeId: string) => {
    const img = e.currentTarget;
    if (img.src.includes('maxresdefault')) {
      img.src = getYouTubeThumbnailFallback(youtubeId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Basketball Hoops Recaps</h1>
            <p className="text-muted-foreground mt-2">Premium basketball video content</p>
          </div>
        </div>

        {/* CONDITIONAL RENDERING BASED ON STATE */}
        {authLoading ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Authenticating...</h3>
              <p className="text-muted-foreground">Checking if you're logged in...</p>
            </CardContent>
          </Card>
        ) : !user ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-yellow-500 text-4xl mb-4">👤</div>
              <h3 className="text-xl font-semibold mb-2">Login Required</h3>
              <p className="text-muted-foreground mb-4">Please log in to access premium content.</p>
              <Button onClick={() => navigate('/videos/hoops-recaps')}>
                Go to Subscription Page
              </Button>
            </CardContent>
          </Card>
        ) : membershipLoading ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Checking Access...</h3>
              <p className="text-muted-foreground">Verifying your membership status...</p>
            </CardContent>
          </Card>
        ) : !hasAccess ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-red-500 text-4xl mb-4">🚫</div>
              <h3 className="text-xl font-semibold mb-2">Access Required</h3>
              <p className="text-muted-foreground mb-4">Upgrade your membership to access premium video content.</p>
              <Button onClick={() => navigate('/videos/hoops-recaps')}>
                Get Access
              </Button>
            </CardContent>
          </Card>
        ) : videosLoading ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Loading Videos...</h3>
              <p className="text-muted-foreground">Fetching your premium content...</p>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold mb-2">Error Loading Videos</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => {
                setError(null);
                setVideosLoading(true);
                fetchVideos();
              }}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : videos.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-blue-500 text-4xl mb-4">📺</div>
              <h3 className="text-xl font-semibold mb-2">No Videos Available</h3>
              <p className="text-muted-foreground mb-4">
                No video content is currently available for this membership.
              </p>
              <Button onClick={() => {
                setVideosLoading(true);
                fetchVideos();
              }} variant="outline">
                Refresh
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
                <Card>
                  <CardContent className="p-0">
                    {selectedVideo && (
                      <>
                        <div 
                          className="aspect-video bg-black rounded-t-lg overflow-hidden relative select-none"
                          onContextMenu={(e) => e.preventDefault()}
                          style={{ userSelect: 'none', WebkitUserSelect: 'none', msUserSelect: 'none' }}
                        >
                          {/* Note: YouTube iframe may generate CSP warnings in console - this is expected and cannot be fixed from client-side */}
                          <iframe
                            src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?origin=https://recruitlook.com&modestbranding=1&rel=0&showinfo=0&autoplay=0&controls=1&disablekb=1`}
                            title={selectedVideo.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onContextMenu={(e) => e.preventDefault()}
                          />
                          {/* User watermark overlay */}
                          {user?.email && (
                            <div 
                              className="absolute bottom-4 right-4 text-white/30 text-xs font-mono bg-black/20 px-2 py-1 rounded backdrop-blur-sm pointer-events-none select-none"
                              style={{ userSelect: 'none' }}
                            >
                              {user.email}
                            </div>
                          )}
                          {/* Prevent right-click overlay */}
                          <div 
                            className="absolute inset-0 pointer-events-none select-none" 
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ userSelect: 'none' }}
                          />
                        </div>
                        <div className="p-6">
                          <h2 className="text-2xl font-bold mb-2">{selectedVideo.title}</h2>
                          {selectedVideo.description && (
                            <p className="text-muted-foreground mb-4">{selectedVideo.description}</p>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

            <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Video Playlist ({videos.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {videos.map((video, index) => (
                        <div
                          key={video.id}
                          className={`p-4 cursor-pointer transition-colors hover:bg-muted ${
                            selectedVideo?.id === video.id ? 'bg-muted border-l-4 border-primary' : ''
                          }`}
                          onClick={() => setSelectedVideo(video)}
                        >
                          <div className="flex items-center gap-3">
                            <img
                              src={getYouTubeThumbnail(video.youtube_id)}
                              alt={video.title}
                              className="w-16 h-12 object-cover rounded"
                              onError={(e) => handleThumbnailError(e, video.youtube_id)}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm leading-tight mb-1 truncate">
                                {video.title}
                              </h4>
                              <div className="flex items-center text-xs text-muted-foreground">
                                <span>Video {index + 1}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoHoopsWatch;